// Phase 0 viewer for the transcript-merge-v2 diagnostic ring buffer
// (`src/lib/dev/devEditLog.ts`). Lives inside the Dev simulator popover.
//
// Purpose: answer the locked-but-unknown question in
// docs/planning/transcript_merge_v2.md — "what's the persisted value when
// a manager clears a field? null or empty string?" — with real session
// data, in-memory, no network, no persistence.
//
// The buffer fills regardless of dev mode (it's a 256-entry array, cheap),
// but this viewer is dev-only because the popover that mounts it is
// already gated on `platform_dev` / `dev` real roles.

import { useEffect, useState } from "react";
import {
  FIELD_EDIT_LOG_CAPACITY,
  clearFieldEditLog,
  getFieldEditLog,
  subscribeFieldEditLog,
  type LeadFieldEditEntry,
} from "@/lib/dev/devEditLog";

export function DevFieldEditLogSection({ containerOpen }: { containerOpen: boolean }) {
  const [entries, setEntries] = useState<readonly LeadFieldEditEntry[]>(getFieldEditLog);

  useEffect(() => {
    if (!containerOpen) return;
    const refresh = () => setEntries([...getFieldEditLog()]);
    refresh();
    return subscribeFieldEditLog(refresh);
  }, [containerOpen]);

  const recent = entries.slice(-25).reverse();
  const empty = recent.length === 0;

  return (
    <section className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50/60 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-950/40">
      <header className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-amber-900 dark:text-amber-200">
            Field edit log
          </h3>
          <p className="text-[10px] leading-snug text-amber-800/80 dark:text-amber-300/80">
            Last {Math.min(recent.length, 25)} of {entries.length} edits (cap {FIELD_EDIT_LOG_CAPACITY}).
            Used to validate transcript-merge-v2 clearing behavior.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            clearFieldEditLog();
            setEntries([]);
          }}
          className="shrink-0 rounded border border-amber-300 bg-white px-2 py-1 text-[10px] font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
        >
          Clear
        </button>
      </header>
      {empty ? (
        <p className="mt-2 text-[10px] italic text-amber-800/70 dark:text-amber-300/70">
          No edits yet — type or clear a lead field to populate the buffer.
        </p>
      ) : (
        <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto pr-1 text-[10px] font-mono text-amber-900 dark:text-amber-200">
          {recent.map((e, idx) => (
            <li
              key={`${e.ts}-${idx}-${e.key}`}
              className="rounded border border-amber-200/70 bg-white/60 px-2 py-1 dark:border-amber-800/50 dark:bg-amber-950/30"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{e.key}</span>
                <span className="text-[9px] text-amber-700 dark:text-amber-400">
                  {new Date(e.ts).toLocaleTimeString()} · after={e.afterType}
                </span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <span className="truncate">
                  <span className="text-amber-700 dark:text-amber-400">before: </span>
                  {formatValue(e.before)}
                </span>
                <span className="truncate">
                  <span className="text-amber-700 dark:text-amber-400">after: </span>
                  {formatValue(e.after)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (v === "") return '""';
  if (typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map((x) => formatValue(x)).join(", ")}]`;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
